export const VIDEO_REFERENCE_BATCH_MAX_ITEMS = 100;
export const VIDEO_REFERENCE_BATCH_TEXT_MAX_CHARS = 500;
export const VIDEO_REFERENCE_BATCH_UPLOADER_EXPLANATION =
  "Each item can include personaId, characterId, productId, optional catalogVariantName, and a trusted referenceUploadId. Put shared identities in a top-level defaults object when appropriate. Creative fields include variant, optional exactText, textStyle, fontAssetId, textPlacement, textAlign, and musicAssetId. Position and maxWidth are normalized from 0–1. Font size, letter spacing, stroke, shadow, and padding are pixels on a 1080px-wide canvas and scale with the video. Embedded fonts support fontFormat values ttf and otf; replace the example fontDataBase64 with the complete Base64 file bytes (without a data URL prefix). Older textStyle exports using percentage x/y/maxWidth, strokeWidth, and boolean shadow are also converted automatically.";
/** Maximum decoded size of one embedded TTF/OTF font. */
export const VIDEO_REFERENCE_EMBEDDED_FONT_MAX_BYTES = 5 * 1024 * 1024;
export const VIDEO_REFERENCE_EMBEDDED_FONT_MAX_BASE64_CHARS =
  Math.ceil(VIDEO_REFERENCE_EMBEDDED_FONT_MAX_BYTES / 3) * 4;

// ── textStyle contract (version 1) ───────────────────────────────────────────

/** Allowed text-anchor values */
export type TextStyleAnchor =
  | "top-left" | "top-center" | "top-right"
  | "middle-left" | "middle-center" | "middle-right"
  | "bottom-left" | "bottom-center" | "bottom-right";

/** Allowed text-align values */
export type TextStyleAlign = "left" | "center" | "right";
export const VIDEO_REFERENCE_TEXT_PLACEMENTS = [
  "top",
  "upper-third",
  "middle",
  "lower-third",
  "bottom",
] as const;
export type VideoReferenceTextPlacement =
  typeof VIDEO_REFERENCE_TEXT_PLACEMENTS[number];
const TEXT_PLACEMENT_VALUES = new Set<string>(VIDEO_REFERENCE_TEXT_PLACEMENTS);

export function applyVideoReferenceTextPlacement(
  style: TextStyle,
  placement: VideoReferenceTextPlacement,
): TextStyle {
  const resolved = {
    top: { position: { x: 0.5, y: 0.14 }, anchor: "top-center" },
    "upper-third": { position: { x: 0.5, y: 1 / 3 }, anchor: "middle-center" },
    middle: { position: { x: 0.5, y: 0.5 }, anchor: "middle-center" },
    "lower-third": { position: { x: 0.5, y: 2 / 3 }, anchor: "middle-center" },
    bottom: { position: { x: 0.5, y: 0.86 }, anchor: "bottom-center" },
  }[placement] as { position: { x: number; y: number }; anchor: TextStyleAnchor };
  return { ...style, position: resolved.position, anchor: resolved.anchor };
}

export function inferVideoReferenceTextPlacement(
  style: Pick<TextStyle, "position" | "anchor"> | null | undefined,
): VideoReferenceTextPlacement {
  const y = style?.position?.y;
  const anchor = style?.anchor;
  if (anchor === "top-center" && y !== undefined && Math.abs(y - 0.14) < 0.03) return "top";
  if (anchor === "bottom-center" && y !== undefined && Math.abs(y - 0.86) < 0.03) return "bottom";
  if (y !== undefined && y < 0.42) return "upper-third";
  if (y !== undefined && y > 0.58) return "lower-third";
  return "middle";
}

/**
 * The editor displays videos with object-contain, while the text renderer
 * treats the source video's intrinsic frame as the coordinate system. Keep
 * this geometry independent of either DOM or canvas APIs so both UI behavior
 * and regression tests use the same conversion.
 */
export interface VideoReferenceDisplayedContentRect {
  left: number;
  top: number;
  width: number;
  height: number;
  /** Scale from the persisted 1080px design canvas to displayed pixels. */
  scale: number;
}

export const VIDEO_REFERENCE_DESIGN_CANVAS_WIDTH = 1080;

export function videoReferenceCanvasScaleForWidth(width: number): number {
  return width / VIDEO_REFERENCE_DESIGN_CANVAS_WIDTH;
}

export function getVideoReferenceDisplayedContentRect(
  containerWidth: number,
  containerHeight: number,
  intrinsicWidth: number,
  intrinsicHeight: number,
): VideoReferenceDisplayedContentRect | null {
  if (
    !Number.isFinite(containerWidth) ||
    !Number.isFinite(containerHeight) ||
    !Number.isFinite(intrinsicWidth) ||
    !Number.isFinite(intrinsicHeight) ||
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    intrinsicWidth <= 0 ||
    intrinsicHeight <= 0
  ) {
    return null;
  }
  const mediaAspect = intrinsicWidth / intrinsicHeight;
  const containerAspect = containerWidth / containerHeight;
  const width = mediaAspect > containerAspect
    ? containerWidth
    : containerHeight * mediaAspect;
  const height = mediaAspect > containerAspect
    ? containerWidth / mediaAspect
    : containerHeight;
  return {
    left: (containerWidth - width) / 2,
    top: (containerHeight - height) / 2,
    width,
    height,
    scale: videoReferenceCanvasScaleForWidth(width),
  };
}

export function videoReferenceCanvasPositionToPreview(
  position: { x: number; y: number },
  contentRect: VideoReferenceDisplayedContentRect,
): { x: number; y: number } {
  return {
    x: contentRect.left + position.x * contentRect.width,
    y: contentRect.top + position.y * contentRect.height,
  };
}

export function videoReferencePreviewPointToCanvasPosition(
  point: { x: number; y: number },
  contentRect: VideoReferenceDisplayedContentRect,
): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(1, (point.x - contentRect.left) / contentRect.width)),
    y: Math.max(0, Math.min(1, (point.y - contentRect.top) / contentRect.height)),
  };
}

export function videoReferenceCanvasTextMetrics(
  fontSize: number,
  maxWidth: number,
  contentRect: VideoReferenceDisplayedContentRect,
): { fontSizePx: number; maxWidthPx: number } {
  return {
    fontSizePx: Math.max(1, fontSize * contentRect.scale),
    maxWidthPx: maxWidth * contentRect.width,
  };
}

export function clampVideoReferenceTextPositionToFrame(input: {
  position: { x: number; y: number };
  anchor: TextStyleAnchor;
  blockWidth: number;
  blockHeight: number;
  paddingX?: number;
  paddingY?: number;
}): { position: { x: number; y: number }; fits: boolean } {
  const horizontal = input.anchor.endsWith("-left")
    ? "left"
    : input.anchor.endsWith("-right")
      ? "right"
      : "center";
  const vertical = input.anchor.startsWith("top-")
    ? "top"
    : input.anchor.startsWith("bottom-")
      ? "bottom"
      : "middle";
  const axisRange = (
    alignment: "left" | "center" | "right" | "top" | "middle" | "bottom",
    size: number,
    padding: number,
  ): { min: number; max: number } => {
    if (alignment === "left" || alignment === "top") {
      return { min: padding, max: 1 - size - padding };
    }
    if (alignment === "right" || alignment === "bottom") {
      return { min: size + padding, max: 1 - padding };
    }
    return {
      min: size / 2 + padding,
      max: 1 - size / 2 - padding,
    };
  };
  const xRange = axisRange(
    horizontal,
    Math.max(0, input.blockWidth),
    Math.max(0, input.paddingX ?? 0),
  );
  const yRange = axisRange(
    vertical,
    Math.max(0, input.blockHeight),
    Math.max(0, input.paddingY ?? 0),
  );
  const xFits = xRange.min <= xRange.max;
  const yFits = yRange.min <= yRange.max;
  return {
    position: {
      x: xFits
        ? Math.max(xRange.min, Math.min(xRange.max, input.position.x))
        : input.position.x,
      y: yFits
        ? Math.max(yRange.min, Math.min(yRange.max, input.position.y))
        : input.position.y,
    },
    fits: xFits && yFits,
  };
}

/** Stroke sub-object */
export interface TextStyleStroke {
  color: string;   // #RRGGBB
  width: number;   // px
  opacity?: number; // 0–1, default 1
}

/** Drop-shadow sub-object */
export interface TextStyleShadow {
  color: string;    // #RRGGBB
  opacity?: number; // 0–1, default 1
  offsetX: number;  // px
  offsetY: number;  // px
  blur?: number;    // px, default 0
}

/** Background-box sub-object */
export interface TextStyleBackground {
  color: string;    // #RRGGBB
  opacity?: number; // 0–1, default 1
  padding?: number; // px, default 0
  borderRadius?: number; // px, default 0
}

/** Full version-1 textStyle object */
export interface TextStyle {
  version: 1;
  fontUrl?: string;        // HTTPS TTF/OTF URL; absent → bundled Inter-Medium
  /**
   * Complete embedded font source. These fields are accepted only together and
   * are mutually exclusive with fontUrl. fontDataBase64 is admission-only:
   * routes snapshot its bytes durably, then remove it before persisting a run.
   */
  fontFamily?: string;
  fontFileName?: string;
  fontFormat?: "ttf" | "otf";
  fontDataBase64?: string;
  fontSize?: number;       // px on a 1080px-wide canvas
  fontWeight?: number;     // 100–900
  color?: string;          // #RRGGBB
  opacity?: number;        // 0–1
  position?: { x: number; y: number }; // 0–1 normalized
  anchor?: TextStyleAnchor;
  align?: TextStyleAlign;
  lineHeight?: number;     // unitless multiplier
  letterSpacing?: number;  // px on a 1080px-wide canvas
  maxWidth?: number;       // normalized width, 0–1
  stroke?: TextStyleStroke;
  shadow?: TextStyleShadow;
  background?: TextStyleBackground;
}

/** Known keys at each level — unknown keys at any level are rejected */
const TEXT_STYLE_TOP_KEYS = new Set([
  "version", "fontUrl", "fontFamily", "fontFileName", "fontFormat",
  "fontDataBase64", "fontSize", "fontWeight", "color", "opacity",
  "position", "anchor", "align", "lineHeight", "letterSpacing", "maxWidth",
  "stroke", "shadow", "background",
]);
const STROKE_KEYS = new Set(["color", "width", "opacity"]);
const SHADOW_KEYS = new Set(["color", "opacity", "offsetX", "offsetY", "blur"]);
const BACKGROUND_KEYS = new Set(["color", "opacity", "padding", "borderRadius"]);
const ANCHOR_VALUES: ReadonlySet<string> = new Set([
  "top-left", "top-center", "top-right",
  "middle-left", "middle-center", "middle-right",
  "bottom-left", "bottom-center", "bottom-right",
]);
const ALIGN_VALUES: ReadonlySet<string> = new Set(["left", "center", "right"]);
const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;
const FONT_FILE_NAME_RE = /^[^/\\\0]{1,255}\.(ttf|otf)$/i;
const BASE64_RE = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
const LEGACY_TEXT_STYLE_KEYS = new Set(["textAlign", "x", "y", "strokeWidth"]);
const LEGACY_SHADOW: TextStyleShadow = {
  color: "#000000",
  opacity: 0.38,
  offsetX: 0,
  offsetY: 4,
  blur: 6,
};

function rejectUnknownKeys(obj: Record<string, unknown>, allowed: Set<string>, ctx: string): void {
  for (const k of Object.keys(obj)) {
    if (!allowed.has(k)) throw new Error(`textStyle: unknown key "${k}" in ${ctx}`);
  }
}

function requireHexColor(v: unknown, field: string): string {
  if (typeof v !== "string" || !HEX_COLOR_RE.test(v)) {
    throw new Error(`textStyle: ${field} must be a #RRGGBB hex color`);
  }
  return v;
}

function requireOpacity(v: unknown, field: string): number {
  if (typeof v !== "number" || v < 0 || v > 1) {
    throw new Error(`textStyle: ${field} must be a number 0–1`);
  }
  return v;
}

function requireNumber(v: unknown, field: string): number {
  if (typeof v !== "number" || !Number.isFinite(v)) {
    throw new Error(`textStyle: ${field} must be a finite number`);
  }
  return v;
}

function requireBoundedNumber(
  v: unknown,
  field: string,
  min: number,
  max: number,
): number {
  const value = requireNumber(v, field);
  if (value < min || value > max) {
    throw new Error(`textStyle: ${field} must be between ${min} and ${max}`);
  }
  return value;
}

function parseStroke(raw: unknown): TextStyleStroke {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("textStyle: stroke must be an object");
  }
  const o = raw as Record<string, unknown>;
  rejectUnknownKeys(o, STROKE_KEYS, "stroke");
  return {
    color: requireHexColor(o.color, "stroke.color"),
    width: requireBoundedNumber(o.width, "stroke.width", 0, 20),
    ...(o.opacity !== undefined ? { opacity: requireOpacity(o.opacity, "stroke.opacity") } : {}),
  };
}

function parseShadow(raw: unknown): TextStyleShadow {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("textStyle: shadow must be an object");
  }
  const o = raw as Record<string, unknown>;
  rejectUnknownKeys(o, SHADOW_KEYS, "shadow");
  return {
    color: requireHexColor(o.color, "shadow.color"),
    offsetX: requireBoundedNumber(o.offsetX, "shadow.offsetX", -100, 100),
    offsetY: requireBoundedNumber(o.offsetY, "shadow.offsetY", -100, 100),
    ...(o.opacity !== undefined ? { opacity: requireOpacity(o.opacity, "shadow.opacity") } : {}),
    ...(o.blur !== undefined ? { blur: requireBoundedNumber(o.blur, "shadow.blur", 0, 50) } : {}),
  };
}

function parseBackground(raw: unknown): TextStyleBackground {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("textStyle: background must be an object");
  }
  const o = raw as Record<string, unknown>;
  rejectUnknownKeys(o, BACKGROUND_KEYS, "background");
  return {
    color: requireHexColor(o.color, "background.color"),
    ...(o.opacity !== undefined ? { opacity: requireOpacity(o.opacity, "background.opacity") } : {}),
    ...(o.padding !== undefined ? { padding: requireBoundedNumber(o.padding, "background.padding", 0, 100) } : {}),
    ...(o.borderRadius !== undefined ? { borderRadius: requireBoundedNumber(o.borderRadius, "background.borderRadius", 0, 100) } : {}),
  };
}

/**
 * Older batch exports used a compact, percentage-based text style shape. Keep
 * that shape readable at the upload boundary only, then validate the resulting
 * strict v1 object normally. A v1 object cannot mix in these aliases.
 */
function normalizeLegacyTextStyle(raw: Record<string, unknown>): Record<string, unknown> {
  const usesLegacyKeys =
    [...LEGACY_TEXT_STYLE_KEYS].some((key) => key in raw) ||
    typeof raw.shadow === "boolean";
  if (!usesLegacyKeys) return raw;
  if (raw.version !== undefined) {
    // Let the normal unknown-key check give a precise failure for v1 payloads.
    return raw;
  }
  const style: Record<string, unknown> = { ...raw, version: 1 };
  if (style.textAlign !== undefined) {
    if (style.align !== undefined) {
      throw new Error("textStyle: textAlign cannot be combined with align");
    }
    style.align = style.textAlign;
    delete style.textAlign;
  }

  const hasX = style.x !== undefined;
  const hasY = style.y !== undefined;
  if (hasX || hasY) {
    if (style.position !== undefined) {
      throw new Error("textStyle: x/y cannot be combined with position");
    }
    if (!hasX || !hasY) {
      throw new Error("textStyle: legacy x and y must be supplied together");
    }
    const x = requireBoundedNumber(style.x, "x", 0, 100);
    const y = requireBoundedNumber(style.y, "y", 0, 100);
    style.position = { x: x / 100, y: y / 100 };
    delete style.x;
    delete style.y;
  }

  if (style.maxWidth !== undefined) {
    const maxWidth = requireBoundedNumber(style.maxWidth, "maxWidth", 0, 100);
    style.maxWidth = maxWidth / 100;
  }
  if (style.strokeWidth !== undefined) {
    if (style.stroke !== undefined) {
      throw new Error("textStyle: strokeWidth cannot be combined with stroke");
    }
    const width = requireBoundedNumber(style.strokeWidth, "strokeWidth", 0, 20);
    style.stroke = { color: "#000000", opacity: 0.82, width };
    delete style.strokeWidth;
  }
  if (typeof style.shadow === "boolean") {
    style.shadow = style.shadow
      ? { ...LEGACY_SHADOW }
      : { color: "#000000", opacity: 0, offsetX: 0, offsetY: 0, blur: 0 };
  }
  return style;
}

/**
 * Parse and validate a raw textStyle object.
 * Throws descriptive errors for unknown keys or bad values.
 */
function parseTextStyleInternal(
  raw: unknown,
  options: { allowEmbeddedFontSnapshot: boolean },
): TextStyle {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("textStyle must be an object");
  }
  const o = normalizeLegacyTextStyle(raw as Record<string, unknown>);
  rejectUnknownKeys(o, TEXT_STYLE_TOP_KEYS, "textStyle");
  if (o.version !== 1) {
    throw new Error(`textStyle: version must be 1 (got ${JSON.stringify(o.version)})`);
  }
  const style: TextStyle = { version: 1 };

  if (o.fontUrl !== undefined) {
    if (typeof o.fontUrl !== "string") {
      throw new Error("textStyle: fontUrl must be an HTTPS URL");
    }
    try {
      const url = new URL(o.fontUrl);
      if (url.protocol !== "https:" || !url.hostname || url.username || url.password) {
        throw new Error("invalid");
      }
      style.fontUrl = url.toString();
    } catch {
      throw new Error("textStyle: fontUrl must be a public HTTPS URL without credentials");
    }
  }
  const embeddedFontKeys = ["fontFamily", "fontFileName", "fontFormat", "fontDataBase64"] as const;
  const suppliedEmbeddedKeys = embeddedFontKeys.filter((key) => o[key] !== undefined);
  if (style.fontUrl && suppliedEmbeddedKeys.length && !options.allowEmbeddedFontSnapshot) {
    throw new Error("textStyle: fontUrl cannot be combined with an embedded font source");
  }
  if (suppliedEmbeddedKeys.length) {
    const metadataKeys = ["fontFamily", "fontFileName", "fontFormat"] as const;
    const missingMetadata = metadataKeys.filter((key) => o[key] === undefined);
    if (missingMetadata.length) {
      throw new Error(
        `textStyle: embedded font requires ${missingMetadata.join(", ")}`,
      );
    }
    const hasData = o.fontDataBase64 !== undefined;
    if (!hasData && !options.allowEmbeddedFontSnapshot) {
      throw new Error(
        "textStyle: embedded font requires fontFamily, fontFileName, fontFormat, and fontDataBase64",
      );
    }
    if (typeof o.fontFamily !== "string" || !o.fontFamily.trim() || o.fontFamily.trim().length > 255) {
      throw new Error("textStyle: fontFamily must be a non-empty name up to 255 characters");
    }
    if (typeof o.fontFileName !== "string" || !FONT_FILE_NAME_RE.test(o.fontFileName)) {
      throw new Error("textStyle: fontFileName must be a safe .ttf or .otf filename");
    }
    if (o.fontFormat !== "ttf" && o.fontFormat !== "otf") {
      throw new Error('textStyle: fontFormat must be "ttf" or "otf"');
    }
    const extension = o.fontFileName.slice(o.fontFileName.lastIndexOf(".") + 1).toLowerCase();
    if (extension !== o.fontFormat) {
      throw new Error("textStyle: fontFileName extension must match fontFormat");
    }
    if (hasData) {
      if (typeof o.fontDataBase64 !== "string" || !o.fontDataBase64) {
        throw new Error("textStyle: fontDataBase64 must be a non-empty Base64 string");
      }
      if (o.fontDataBase64.length > VIDEO_REFERENCE_EMBEDDED_FONT_MAX_BASE64_CHARS) {
        throw new Error(
          `textStyle: fontDataBase64 is too large (max ${VIDEO_REFERENCE_EMBEDDED_FONT_MAX_BYTES / 1024 / 1024} MiB decoded)`,
        );
      }
      if (
        o.fontDataBase64.length % 4 !== 0 ||
        !BASE64_RE.test(o.fontDataBase64)
      ) {
        throw new Error("textStyle: fontDataBase64 must be canonical Base64 without a data URL prefix");
      }
      style.fontDataBase64 = o.fontDataBase64;
    }
    style.fontFamily = o.fontFamily.trim();
    style.fontFileName = o.fontFileName;
    style.fontFormat = o.fontFormat;
  }
  if (o.fontSize !== undefined) style.fontSize = requireBoundedNumber(o.fontSize, "fontSize", 12, 240);
  if (o.fontWeight !== undefined) {
    const w = requireNumber(o.fontWeight, "fontWeight");
    if (!Number.isInteger(w) || w < 100 || w > 900) {
      throw new Error("textStyle: fontWeight must be an integer from 100–900");
    }
    style.fontWeight = w;
  }
  if (o.color !== undefined) style.color = requireHexColor(o.color, "color");
  if (o.opacity !== undefined) style.opacity = requireOpacity(o.opacity, "opacity");
  if (o.position !== undefined) {
    if (!o.position || typeof o.position !== "object" || Array.isArray(o.position)) {
      throw new Error("textStyle: position must be an object");
    }
    const pos = o.position as Record<string, unknown>;
    // position object only allows x and y
    for (const k of Object.keys(pos)) {
      if (k !== "x" && k !== "y") throw new Error(`textStyle: unknown key "${k}" in position`);
    }
    const x = requireNumber(pos.x, "position.x");
    const y = requireNumber(pos.y, "position.y");
    if (x < 0 || x > 1 || y < 0 || y > 1) {
      throw new Error("textStyle: position x and y must be 0–1 (normalized)");
    }
    style.position = { x, y };
  }
  if (o.anchor !== undefined) {
    if (typeof o.anchor !== "string" || !ANCHOR_VALUES.has(o.anchor)) {
      throw new Error(
        `textStyle: anchor must be one of: ${[...ANCHOR_VALUES].join(", ")}`,
      );
    }
    style.anchor = o.anchor as TextStyleAnchor;
  }
  if (o.align !== undefined) {
    if (typeof o.align !== "string" || !ALIGN_VALUES.has(o.align)) {
      throw new Error(
        `textStyle: align must be one of: ${[...ALIGN_VALUES].join(", ")}`,
      );
    }
    style.align = o.align as TextStyleAlign;
  }
  if (o.lineHeight !== undefined) style.lineHeight = requireBoundedNumber(o.lineHeight, "lineHeight", 0.8, 3);
  if (o.letterSpacing !== undefined) style.letterSpacing = requireBoundedNumber(o.letterSpacing, "letterSpacing", -20, 50);
  if (o.maxWidth !== undefined) style.maxWidth = requireBoundedNumber(o.maxWidth, "maxWidth", 0.1, 1);
  if (o.stroke !== undefined) style.stroke = parseStroke(o.stroke);
  if (o.shadow !== undefined) style.shadow = parseShadow(o.shadow);
  if (o.background !== undefined) style.background = parseBackground(o.background);

  return {
    ...DEFAULT_TEXT_STYLE,
    ...style,
    position: style.position
      ? { ...style.position }
      : { ...(DEFAULT_TEXT_STYLE.position || { x: 0.5, y: 0.5 }) },
  };
}

/**
 * Parse an untrusted batch/API textStyle. An embedded source must contain every
 * metadata field plus canonical Base64 font bytes.
 */
export function parseTextStyle(raw: unknown): TextStyle {
  return parseTextStyleInternal(raw, { allowEmbeddedFontSnapshot: false });
}

/**
 * Parse a server-created textStyle snapshot. The durable font bytes have
 * already been removed from its JSON, while the declared family/file/format
 * remain so the renderer can prove it selected the intended face.
 */
export function parsePersistedTextStyle(raw: unknown): TextStyle {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const persisted = raw as Record<string, unknown>;
    if (
      typeof persisted.fontUrl === "string" &&
      persisted.fontUrl.startsWith("r2-private://")
    ) {
      const { fontUrl: _privateFontLocator, ...safeStyle } = persisted;
      return parseTextStyleInternal(safeStyle, { allowEmbeddedFontSnapshot: true });
    }
  }
  return parseTextStyleInternal(raw, { allowEmbeddedFontSnapshot: true });
}

/** Remove admission-only font bytes before writing a textStyle to a run row. */
export function stripEmbeddedFontData(style: TextStyle): TextStyle {
  const { fontDataBase64: _fontDataBase64, ...snapshot } = style;
  return snapshot;
}

/** Default textStyle applied when exactText is present but no style is given */
export const DEFAULT_TEXT_STYLE: Readonly<TextStyle> = Object.freeze({
  version: 1 as const,
  fontSize: 55,
  fontWeight: 700,
  color: "#FFFFFF",
  opacity: 1,
  position: Object.freeze({ x: 0.5, y: 0.14 }),
  anchor: "top-center" as TextStyleAnchor,
  align: "center" as TextStyleAlign,
  lineHeight: 1.15,
  letterSpacing: 0,
  maxWidth: 1,
  stroke: Object.freeze({ color: "#000000", width: 2, opacity: 0.82 }),
  shadow: Object.freeze({ color: "#000000", opacity: 0.38, offsetX: 0, offsetY: 4, blur: 6 }),
});

/**
 * The bounded, server-owned recipe used by the inline Video Reference quick
 * editor. It intentionally contains no URLs or font bytes; those are resolved
 * and snapshotted by the API before the recipe is persisted.
 */
export interface VideoReferenceQuickEditRecipe {
  version: 1;
  trimStartSec: number;
  trimEndSec: number;
  /**
   * "replace" is admitted by the API only after musicAssetId has been
   * authorized. The accompanying snapshot is server-created, never supplied
   * by the browser as a URL or byte payload.
   */
  audio: "keep" | "mute" | "replace";
  audioAssetId?: string;
  audioAssetLabel?: string;
  audioAssetUrl?: string;
  audioAssetSha256?: string;
  audioAssetFormat?: "mp3" | "wav";
  exactText: string | null;
  textStyle: TextStyle | null;
  /** Source-timeline interval for the deterministic text layer. */
  textStartSec?: number;
  textEndSec?: number;
}

export interface VideoReferenceQuickEditTimeline {
  /** Absolute offset into the immutable clean source. */
  sourceStartSec: number;
  /** Absolute, exclusive end bound in the immutable clean source. */
  sourceEndSec: number;
  /** Local duration shown by the editor for the currently applied source window. */
  durationSec: number;
}

/**
 * Resolve the only source window the browser may edit. A successful prior
 * recipe narrows later edits to its applied source interval; otherwise the
 * precise probed reference duration is the full window.
 */
export function resolveVideoReferenceQuickEditTimeline(input: {
  referenceDurationSec: unknown;
  quickEditRecipeJson?: unknown;
}): VideoReferenceQuickEditTimeline | null {
  const sourceDurationSec = Number(input.referenceDurationSec);
  if (!Number.isFinite(sourceDurationSec) || sourceDurationSec <= 0) return null;

  const recipe = input.quickEditRecipeJson;
  if (recipe && typeof recipe === "object" && !Array.isArray(recipe)) {
    const sourceStartSec = Number((recipe as Record<string, unknown>).trimStartSec);
    const sourceEndSec = Number((recipe as Record<string, unknown>).trimEndSec);
    if (
      Number.isFinite(sourceStartSec) &&
      Number.isFinite(sourceEndSec) &&
      sourceStartSec >= 0 &&
      sourceEndSec <= sourceDurationSec &&
      sourceEndSec - sourceStartSec >= 0.25
    ) {
      return {
        sourceStartSec,
        sourceEndSec,
        durationSec: sourceEndSec - sourceStartSec,
      };
    }
  }

  return {
    sourceStartSec: 0,
    sourceEndSec: sourceDurationSec,
    durationSec: sourceDurationSec,
  };
}

/**
 * Compatibility for the deployed editor that submitted an untouched full
 * range using the run's rounded display duration. This deliberately does not
 * forgive arbitrary overshoots, non-zero starts, or edits to an already
 * applied recipe.
 */
export function normalizeLegacyVideoReferenceQuickEditFullLength<
  T extends Record<string, unknown>,
>(
  raw: T,
  input: {
    referenceDurationSec: unknown;
    displayDurationSec: unknown;
    hasAppliedRecipe: boolean;
  },
): T {
  if (input.hasAppliedRecipe) return raw;
  const sourceDurationSec = Number(input.referenceDurationSec);
  const displayDurationSec = Number(input.displayDurationSec);
  const trimStartSec = raw.trimStartSec;
  const trimEndSec = raw.trimEndSec;
  if (
    !Number.isFinite(sourceDurationSec) ||
    sourceDurationSec <= 0 ||
    !Number.isFinite(displayDurationSec) ||
    displayDurationSec <= sourceDurationSec ||
    Math.abs(displayDurationSec - Math.round(sourceDurationSec)) > 1e-6 ||
    trimStartSec !== 0 ||
    typeof trimEndSec !== "number" ||
    Math.abs(trimEndSec - displayDurationSec) > 1e-6
  ) {
    return raw;
  }
  return {
    ...raw,
    trimEndSec: sourceDurationSec,
    ...(typeof raw.textEndSec === "number" &&
    Math.abs(raw.textEndSec - displayDurationSec) <= 1e-6
      ? { textEndSec: sourceDurationSec }
      : {}),
  };
}

/**
 * Parse a browser recipe against both the immutable source duration and the
 * narrower server-owned window left by a successfully applied quick edit.
 */
export function parseVideoReferenceQuickEditRecipeForRun(
  raw: Record<string, unknown>,
  input: {
    referenceDurationSec: unknown;
    displayDurationSec: unknown;
    appliedRecipeJson?: unknown;
  },
): VideoReferenceQuickEditRecipe {
  const timeline = resolveVideoReferenceQuickEditTimeline({
    referenceDurationSec: input.referenceDurationSec,
    quickEditRecipeJson: input.appliedRecipeJson,
  });
  if (!timeline) {
    throw new Error("quickEditRecipe: source duration is invalid");
  }
  const legacyNormalized = normalizeLegacyVideoReferenceQuickEditFullLength(raw, {
    referenceDurationSec: input.referenceDurationSec,
    displayDurationSec: input.displayDurationSec,
    hasAppliedRecipe: !!input.appliedRecipeJson,
  });
  const epsilon = 1e-6;
  const snapToBound = (value: unknown, bound: number): unknown =>
    typeof value === "number" && Math.abs(value - bound) <= epsilon
      ? bound
      : value;
  const boundaryNormalized = {
    ...legacyNormalized,
    trimStartSec: snapToBound(
      legacyNormalized.trimStartSec,
      timeline.sourceStartSec,
    ),
    trimEndSec: snapToBound(
      legacyNormalized.trimEndSec,
      timeline.sourceEndSec,
    ),
    ...(legacyNormalized.textStartSec === undefined
      ? {}
      : {
          textStartSec: snapToBound(
            legacyNormalized.textStartSec,
            timeline.sourceStartSec,
          ),
        }),
    ...(legacyNormalized.textEndSec === undefined
      ? {}
      : {
          textEndSec: snapToBound(
            legacyNormalized.textEndSec,
            timeline.sourceEndSec,
          ),
        }),
  };
  const recipe = parseVideoReferenceQuickEditRecipe(
    boundaryNormalized,
    Number(input.referenceDurationSec),
  );
  if (
    recipe.trimStartSec < timeline.sourceStartSec ||
    recipe.trimEndSec > timeline.sourceEndSec
  ) {
    throw new Error(
      `quickEditRecipe: trim range must stay within the current ${timeline.sourceStartSec.toFixed(3)}–${timeline.sourceEndSec.toFixed(3)} second edit window`,
    );
  }
  return recipe;
}

export function parseVideoReferenceQuickEditRecipe(
  raw: unknown,
  sourceDurationSec: number,
): VideoReferenceQuickEditRecipe {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("quickEditRecipe must be an object");
  }
  const o = raw as Record<string, unknown>;
  const keys = new Set([
    "version", "trimStartSec", "trimEndSec", "audio", "exactText", "textStyle",
    "textStartSec", "textEndSec",
    "audioAssetId", "audioAssetLabel", "audioAssetUrl", "audioAssetSha256",
    "audioAssetFormat",
  ]);
  for (const key of Object.keys(o)) {
    if (!keys.has(key)) throw new Error(`quickEditRecipe: unknown key "${key}"`);
  }
  if (o.version !== 1) throw new Error("quickEditRecipe: version must be 1");
  const duration = Number(sourceDurationSec);
  if (!Number.isFinite(duration) || duration <= 0) {
    throw new Error("quickEditRecipe: source duration is invalid");
  }
  const rawStart = o.trimStartSec;
  const rawEnd = o.trimEndSec;
  if (
    typeof rawStart !== "number" ||
    !Number.isFinite(rawStart) ||
    typeof rawEnd !== "number" ||
    !Number.isFinite(rawEnd)
  ) {
    throw new Error("quickEditRecipe: trimStartSec and trimEndSec must be finite numbers");
  }
  // Reopening an already-trimmed result composes its clean-source offset with
  // the visible duration. Decimal media durations can then land a few machine
  // epsilons outside the same boundary (for example 0.1 + 25.85). Snap only
  // that arithmetic residue; real out-of-range requests must still fail.
  const boundaryEpsilonSec = 1e-6;
  const start =
    rawStart < 0 && rawStart >= -boundaryEpsilonSec ? 0 : rawStart;
  const end =
    rawEnd > duration && rawEnd <= duration + boundaryEpsilonSec
      ? duration
      : rawEnd;
  if (start < 0 || end > duration || end <= start) {
    throw new Error(`quickEditRecipe: trim range must be within 0–${duration.toFixed(3)} seconds`);
  }
  if (end - start < 0.25) {
    throw new Error("quickEditRecipe: the edited video must be at least 0.25 seconds long");
  }
  if (o.audio !== "keep" && o.audio !== "mute" && o.audio !== "replace") {
    throw new Error('quickEditRecipe: audio must be "keep", "mute", or "replace"');
  }
  let audioSnapshot: Pick<
    VideoReferenceQuickEditRecipe,
    "audioAssetId" | "audioAssetLabel" | "audioAssetUrl" | "audioAssetSha256" | "audioAssetFormat"
  > = {};
  if (o.audio === "replace") {
    if (
      typeof o.audioAssetId !== "string" ||
      !o.audioAssetId ||
      o.audioAssetId.length > 120 ||
      typeof o.audioAssetLabel !== "string" ||
      !o.audioAssetLabel ||
      o.audioAssetLabel.length > 255 ||
      typeof o.audioAssetUrl !== "string" ||
      !o.audioAssetUrl ||
      typeof o.audioAssetSha256 !== "string" ||
      !/^[a-f0-9]{64}$/.test(o.audioAssetSha256) ||
      (o.audioAssetFormat !== "mp3" && o.audioAssetFormat !== "wav")
    ) {
      throw new Error("quickEditRecipe: replacement audio is missing a valid server snapshot");
    }
    audioSnapshot = {
      audioAssetId: o.audioAssetId,
      audioAssetLabel: o.audioAssetLabel,
      audioAssetUrl: o.audioAssetUrl,
      audioAssetSha256: o.audioAssetSha256,
      audioAssetFormat: o.audioAssetFormat,
    };
  } else if (
    o.audioAssetId !== undefined ||
    o.audioAssetLabel !== undefined ||
    o.audioAssetUrl !== undefined ||
    o.audioAssetSha256 !== undefined ||
    o.audioAssetFormat !== undefined
  ) {
    throw new Error("quickEditRecipe: an audio snapshot is only allowed for replacement audio");
  }
  let exactText: string | null = null;
  if (o.exactText !== null && o.exactText !== undefined) {
    if (typeof o.exactText !== "string" || o.exactText.length > 500) {
      throw new Error("quickEditRecipe: exactText must be at most 500 characters");
    }
    exactText = o.exactText;
  }
  let textStyle: TextStyle | null = null;
  if (o.textStyle !== null && o.textStyle !== undefined) {
    textStyle = parsePersistedTextStyle(o.textStyle);
  }
  if (exactText && !textStyle) {
    throw new Error("quickEditRecipe: textStyle is required when exactText is set");
  }
  let textStartSec: number | undefined;
  let textEndSec: number | undefined;
  if (o.textStartSec !== undefined || o.textEndSec !== undefined) {
    if (
      typeof o.textStartSec !== "number" ||
      !Number.isFinite(o.textStartSec) ||
      typeof o.textEndSec !== "number" ||
      !Number.isFinite(o.textEndSec)
    ) {
      throw new Error("quickEditRecipe: textStartSec and textEndSec must be finite numbers");
    }
    textStartSec =
      o.textStartSec < start && o.textStartSec >= start - boundaryEpsilonSec
        ? start
        : o.textStartSec;
    textEndSec =
      o.textEndSec > end && o.textEndSec <= end + boundaryEpsilonSec
        ? end
        : o.textEndSec;
    if (
      textStartSec < start ||
      textEndSec > end ||
      textEndSec <= textStartSec ||
      textEndSec - textStartSec < 0.25
    ) {
      throw new Error("quickEditRecipe: text timing must stay inside the trim range and last at least 0.25 seconds");
    }
  } else if (exactText) {
    // Older recipes predate the timeline. Their text was visible for the
    // entire rendered clip, so make that behavior explicit when they are read.
    textStartSec = start;
    textEndSec = end;
  }
  return {
    version: 1,
    trimStartSec: start,
    trimEndSec: end,
    audio: o.audio,
    ...audioSnapshot,
    exactText,
    textStyle,
    ...(textStartSec === undefined ? {} : { textStartSec, textEndSec }),
  };
}

/**
 * Example textStyle object for UI help-text/placeholders.
 * Shows a representative version-1 style with all common fields.
 */
export const VIDEO_REFERENCE_TEXT_STYLE_EXAMPLE: TextStyle = {
  version: 1,
  fontFamily: "Brand Sans",
  fontFileName: "BrandSans-Bold.ttf",
  fontFormat: "ttf",
  fontDataBase64: "AAEAAA...", // Base64 TTF/OTF bytes; supports only "ttf" and "otf"
  fontSize: 55,
  fontWeight: 700,
  color: "#FFFFFF",
  opacity: 1,
  position: { x: 0.5, y: 0.8 },
  anchor: "bottom-center",
  align: "center",
  lineHeight: 1.2,
  letterSpacing: 0,
  maxWidth: 1,
  stroke: { color: "#000000", width: 2, opacity: 0.8 },
  shadow: { color: "#000000", opacity: 0.5, offsetX: 2, offsetY: 2, blur: 4 },
};

// ── Batch variant types ───────────────────────────────────────────────────────

export type VideoReferenceBatchVariant = {
  promptType?: "general" | "ugc" | "3d";
  promotedItemType?: "product" | "offer" | "none";
  offerId?: string;
  /** Brand-scoped identities resolved independently for this run. */
  personaId?: string;
  characterId?: string;
  productId?: string;
  catalogVariantName?: string | null;
  /** Stable ID from the brand's trusted reference upload library. */
  referenceUploadId?: string;
  /** Creative-direction variant retained under its legacy JSON key. */
  variant: string;
  exactText?: string;
  textStyle?: TextStyle;
  fontAssetId?: string;
  textPlacement?: VideoReferenceTextPlacement;
  /** Legacy horizontal alignment; retained for existing JSON/API clients. */
  textAlign?: TextStyleAlign;
  musicAssetId?: string;
};

export type VideoReferenceBatchDefaults = {
  promptType?: "general" | "ugc" | "3d";
  promotedItemType?: "product" | "offer" | "none";
  offerId?: string;
  personaId?: string;
  characterId?: string;
  productId?: string;
  catalogVariantName?: string | null;
  referenceUploadId?: string;
  fontAssetId?: string;
  textPlacement?: VideoReferenceTextPlacement;
  musicAssetId?: string;
};

export type VideoReferenceBatchValueSource =
  | "json"
  | "form"
  | "textStyle"
  | "automatic"
  | "none";

export type VideoReferenceBatchResolutionSource = {
  font: VideoReferenceBatchValueSource;
  placement: VideoReferenceBatchValueSource;
  alignment: VideoReferenceBatchValueSource;
  music: VideoReferenceBatchValueSource;
};

export type VideoReferenceBatchResolution = {
  items: VideoReferenceBatchVariant[];
  sources: VideoReferenceBatchResolutionSource[];
};

export type VideoReferenceBatchResolutionOptions = {
  /**
   * Bind every new item to the form-level product/catalog variant and reject
   * legacy per-item product fields. Leave false only for parsing old exports.
   */
  enforceFormProduct?: boolean;
  /** Allow role-only entries; admission validates the selected role afterward. */
  allowRoleOnly?: boolean;
};

function parseAssetId(
  raw: unknown,
  field: "fontAssetId" | "musicAssetId",
  index?: number,
): string | undefined {
  if (raw === null || raw === undefined || raw === "") return undefined;
  if (typeof raw !== "string" || !raw.trim() || raw.trim().length > 120) {
    throw new Error(
      `${index === undefined ? "Batch default" : `Item ${index + 1}`} ${field} must be a non-empty asset ID`,
    );
  }
  return raw.trim();
}

function parseBatchIdentityId(
  raw: unknown,
  field: "personaId" | "characterId" | "productId" | "offerId" | "referenceUploadId",
  index?: number,
): string | undefined {
  if (raw === null || raw === undefined || raw === "") return undefined;
  if (typeof raw !== "string" || !raw.trim() || raw.trim().length > 120) {
    throw new Error(
      `${index === undefined ? "Batch default" : `Item ${index + 1}`} ${field} must be a non-empty ID`,
    );
  }
  return raw.trim();
}
function parseBatchCatalogVariantName(
  raw: unknown,
  index?: number,
): string | null | undefined {
  if (raw === undefined) return undefined;
  if (raw === null || raw === "") return null;
  if (typeof raw !== "string" || !raw.trim() || raw.trim().length > 255) {
    throw new Error(
      `${index === undefined ? "Batch default" : `Item ${index + 1}`} catalogVariantName must be a product variant name up to 255 characters`,
    );
  }
  return raw.trim();
}

function rawTextStyleHasFontSource(raw: unknown): boolean {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return false;
  return ["fontUrl", "fontFamily", "fontFileName", "fontFormat", "fontDataBase64"]
    .some((key) => key in (raw as Record<string, unknown>));
}

function rawTextStyleHasAlignment(raw: unknown): boolean {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return false;
  return "align" in (raw as Record<string, unknown>) ||
    "textAlign" in (raw as Record<string, unknown>);
}

function rawTextStyleHasPlacement(raw: unknown): boolean {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return false;
  return ["position", "anchor", "x", "y"]
    .some((key) => key in (raw as Record<string, unknown>));
}

export function normalizeVideoReferenceBatchVariants(
  value: unknown,
  allowUnspecifiedItemRole = false,
): VideoReferenceBatchVariant[] {
  const source = videoReferenceBatchSourceItems(value);
  if (!source) {
    throw new Error('JSON must be an array or an object with an "items" (or legacy "variants") array');
  }
  if (source.length < 1 || source.length > VIDEO_REFERENCE_BATCH_MAX_ITEMS) {
    throw new Error(`JSON must contain between 1 and ${VIDEO_REFERENCE_BATCH_MAX_ITEMS} variants`);
  }
  return source.map((item: unknown, index: number) => {
    const itemObj = item && typeof item === "object" && !Array.isArray(item)
      ? item as Record<string, unknown>
      : null;
    const personaId = parseBatchIdentityId(itemObj?.personaId, "personaId", index);
    const characterId = parseBatchIdentityId(itemObj?.characterId, "characterId", index);
    const productId = parseBatchIdentityId(itemObj?.productId, "productId", index);
    const offerId = parseBatchIdentityId(itemObj?.offerId, "offerId", index);
    const promptType = itemObj?.promptType;
    const promotedItemType = itemObj?.promotedItemType;
    if (promptType !== undefined && !["general", "ugc", "3d"].includes(promptType as string)) throw new Error(`Item ${index + 1} has invalid promptType`);
    if (promotedItemType !== undefined && !["product", "offer", "none"].includes(promotedItemType as string)) throw new Error(`Item ${index + 1} has invalid promotedItemType`);
    const referenceUploadId = parseBatchIdentityId(
      itemObj?.referenceUploadId,
      "referenceUploadId",
      index,
    );
    const hasCatalogVariantName = Boolean(
      itemObj &&
      (
        Object.prototype.hasOwnProperty.call(itemObj, "catalogVariantName") ||
        Object.prototype.hasOwnProperty.call(itemObj, "selectedVariantName")
      )
    );
    const canonicalCatalogVariantName = parseBatchCatalogVariantName(
      itemObj?.catalogVariantName,
      index,
    );
    const legacyCatalogVariantName = parseBatchCatalogVariantName(
      itemObj?.selectedVariantName,
      index,
    );
    if (
      canonicalCatalogVariantName !== undefined &&
      legacyCatalogVariantName !== undefined &&
      canonicalCatalogVariantName !== legacyCatalogVariantName
    ) {
      throw new Error(
        `Item ${index + 1} catalogVariantName conflicts with selectedVariantName`,
      );
    }
    const catalogVariantName =
      canonicalCatalogVariantName !== undefined
        ? canonicalCatalogVariantName
        : legacyCatalogVariantName;
    // Support plain string items and object items.
    const variant = typeof item === "string"
      ? item.trim()
      : itemObj && typeof itemObj.variant === "string"
        ? itemObj.variant.trim()
        : "";
    if (!variant && !productId && !offerId && promotedItemType !== "none" && !allowUnspecifiedItemRole) {
      throw new Error(
        `Item ${index + 1} needs a non-empty creative-direction "variant" or a productId`,
      );
    }
    if (variant.length > VIDEO_REFERENCE_BATCH_TEXT_MAX_CHARS) {
      throw new Error(`Item ${index + 1} creative-direction variant exceeds the ${VIDEO_REFERENCE_BATCH_TEXT_MAX_CHARS}-character limit`);
    }

    const fontAssetId = parseAssetId(itemObj?.fontAssetId, "fontAssetId", index);
    const musicAssetId = parseAssetId(itemObj?.musicAssetId, "musicAssetId", index);
    let textAlign: TextStyleAlign | undefined;
    if (itemObj?.textAlign !== undefined && itemObj.textAlign !== null && itemObj.textAlign !== "") {
      if (typeof itemObj.textAlign !== "string" || !ALIGN_VALUES.has(itemObj.textAlign)) {
        throw new Error(`Item ${index + 1} textAlign must be left, center, or right`);
      }
      textAlign = itemObj.textAlign as TextStyleAlign;
    }
    let textPlacement: VideoReferenceTextPlacement | undefined;
    if (
      itemObj?.textPlacement !== undefined &&
      itemObj.textPlacement !== null &&
      itemObj.textPlacement !== ""
    ) {
      if (
        typeof itemObj.textPlacement !== "string" ||
        !TEXT_PLACEMENT_VALUES.has(itemObj.textPlacement)
      ) {
        throw new Error(
          `Item ${index + 1} textPlacement must be top, upper-third, middle, lower-third, or bottom`,
        );
      }
      textPlacement = itemObj.textPlacement as VideoReferenceTextPlacement;
    }

    // exactText: must be byte/code-unit identical — no trim, no normalize, no truncate.
    // null represents absence. Whitespace-only is preserved as-is (not collapsed).
    let exactText: string | null = null;
    if (itemObj && "exactText" in itemObj) {
      const raw = itemObj.exactText;
      if (raw === null || raw === undefined) {
        exactText = null;
      } else if (typeof raw !== "string") {
        throw new Error(`Item ${index + 1} exactText must be a string or null`);
      } else if (raw.length > VIDEO_REFERENCE_BATCH_TEXT_MAX_CHARS) {
        throw new Error(`Item ${index + 1} exactText exceeds the ${VIDEO_REFERENCE_BATCH_TEXT_MAX_CHARS}-character limit`);
      } else if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/.test(raw)) {
        throw new Error(`Item ${index + 1} exactText contains unsupported control characters`);
      } else {
        exactText = raw; // byte/code-unit identical — no mutation
      }
    }

    // textStyle: version-1 strict contract.
    let textStyle: TextStyle | undefined;
    if (itemObj && "textStyle" in itemObj) {
      if (itemObj.textStyle === null || itemObj.textStyle === undefined) {
        textStyle = undefined;
      } else {
        // textStyle without exactText is rejected.
        if (exactText === null) {
          throw new Error(
            `Item ${index + 1} has textStyle but no exactText — textStyle requires exactText`,
          );
        }
        textStyle = parseTextStyle(itemObj.textStyle);
      }
    }
    if (fontAssetId && rawTextStyleHasFontSource(itemObj?.textStyle)) {
      throw new Error(
        `Item ${index + 1} fontAssetId cannot be combined with textStyle font fields`,
      );
    }
    if (textAlign && rawTextStyleHasAlignment(itemObj?.textStyle)) {
      throw new Error(
        `Item ${index + 1} textAlign cannot be combined with textStyle alignment fields`,
      );
    }
    if (textPlacement && rawTextStyleHasPlacement(itemObj?.textStyle)) {
      throw new Error(
        `Item ${index + 1} textPlacement cannot be combined with textStyle position fields`,
      );
    }
    if (exactText === null && (fontAssetId || textAlign || textPlacement)) {
      throw new Error(
        `Item ${index + 1} fontAssetId/textAlign/textPlacement require exactText`,
      );
    }

    // exactText without textStyle gets defaults.
    if (exactText !== null && textStyle === undefined) {
      textStyle = {
        ...DEFAULT_TEXT_STYLE,
        position: { ...(DEFAULT_TEXT_STYLE.position || { x: 0.5, y: 0.14 }) },
        stroke: DEFAULT_TEXT_STYLE.stroke ? { ...DEFAULT_TEXT_STYLE.stroke } : undefined,
        shadow: DEFAULT_TEXT_STYLE.shadow ? { ...DEFAULT_TEXT_STYLE.shadow } : undefined,
      };
    }
    if (textStyle && textAlign) textStyle.align = textAlign;
    if (textStyle && textPlacement) {
      textStyle = applyVideoReferenceTextPlacement(textStyle, textPlacement);
    }

    return {
      ...(personaId ? { personaId } : {}),
      ...(characterId ? { characterId } : {}),
      ...(productId ? { productId } : {}),
      ...(offerId ? { offerId } : {}),
      ...(promptType ? { promptType: promptType as VideoReferenceBatchVariant["promptType"] } : {}),
      ...(promotedItemType ? { promotedItemType: promotedItemType as VideoReferenceBatchVariant["promotedItemType"] } : {}),
      ...(hasCatalogVariantName ? { catalogVariantName: catalogVariantName ?? null } : {}),
      ...(referenceUploadId ? { referenceUploadId } : {}),
      variant,
      ...(exactText !== null ? { exactText } : {}),
      ...(textStyle !== undefined ? { textStyle } : {}),
      ...(fontAssetId ? { fontAssetId } : {}),
      ...(textPlacement ? { textPlacement } : {}),
      ...(textAlign ? { textAlign } : {}),
      ...(musicAssetId ? { musicAssetId } : {}),
    };
  });
}

function videoReferenceBatchSourceItems(value: unknown): unknown[] | null {
  return Array.isArray(value)
    ? value
    : value && typeof value === "object" && Array.isArray((value as any).items)
      ? (value as any).items
      : value && typeof value === "object" && Array.isArray((value as any).variants)
        ? (value as any).variants
      : null;
}

function normalizeVideoReferenceBatchDefaults(
  defaults: VideoReferenceBatchDefaults | null | undefined,
): VideoReferenceBatchDefaults {
  if (defaults === null || defaults === undefined) {
    return {};
  }
  if (typeof defaults !== "object" || Array.isArray(defaults)) {
    throw new Error("Batch defaults must be an object");
  }
  const fontAssetId = parseAssetId(defaults.fontAssetId, "fontAssetId");
  const musicAssetId = parseAssetId(defaults.musicAssetId, "musicAssetId");
  const personaId = parseBatchIdentityId(defaults.personaId, "personaId");
  const characterId = parseBatchIdentityId(defaults.characterId, "characterId");
  const productId = parseBatchIdentityId(defaults.productId, "productId");
  const offerId = parseBatchIdentityId(defaults.offerId, "offerId");
  if (defaults.promptType !== undefined && !["general", "ugc", "3d"].includes(defaults.promptType)) throw new Error("Batch default has invalid promptType");
  if (defaults.promotedItemType !== undefined && !["product", "offer", "none"].includes(defaults.promotedItemType)) throw new Error("Batch default has invalid promotedItemType");
  const referenceUploadId = parseBatchIdentityId(
    defaults.referenceUploadId,
    "referenceUploadId",
  );
  const hasCatalogVariantName = Object.prototype.hasOwnProperty.call(
    defaults,
    "catalogVariantName",
  );
  const catalogVariantName = parseBatchCatalogVariantName(
    defaults.catalogVariantName,
  );
  const rawPlacement = defaults.textPlacement as unknown;
  if (
    rawPlacement !== undefined &&
    rawPlacement !== null &&
    rawPlacement !== "" &&
    (typeof rawPlacement !== "string" || !TEXT_PLACEMENT_VALUES.has(rawPlacement))
  ) {
    throw new Error(
      "Batch default textPlacement must be top, upper-third, middle, lower-third, or bottom",
    );
  }
  return {
    ...(personaId ? { personaId } : {}),
    ...(characterId ? { characterId } : {}),
    ...(productId ? { productId } : {}),
    ...(offerId ? { offerId } : {}),
    ...(defaults.promptType ? { promptType: defaults.promptType } : {}),
    ...(defaults.promotedItemType ? { promotedItemType: defaults.promotedItemType } : {}),
    ...(hasCatalogVariantName ? { catalogVariantName: catalogVariantName ?? null } : {}),
    ...(referenceUploadId ? { referenceUploadId } : {}),
    ...(fontAssetId ? { fontAssetId } : {}),
    ...(musicAssetId ? { musicAssetId } : {}),
    ...(rawPlacement ? { textPlacement: rawPlacement as VideoReferenceTextPlacement } : {}),
  };
}

/**
 * Resolves imported batch entries with form-level defaults. This function must
 * receive the original imported shape (rather than a previously normalized
 * result) so it can distinguish explicit JSON text-style sources from defaults.
 *
 * Font and vertical-placement defaults only apply to entries with exact text.
 * A JSON asset ID or the corresponding textStyle source always wins. Music is
 * independent of text and therefore applies to every entry without JSON music.
 */
export function resolveVideoReferenceBatchVariants(
  value: unknown,
  defaults?: VideoReferenceBatchDefaults | null,
  options: VideoReferenceBatchResolutionOptions = {},
): VideoReferenceBatchResolution {
  const sourceItems = videoReferenceBatchSourceItems(value);
  if (!sourceItems) {
    throw new Error('JSON must be an array or an object with an "items" (or legacy "variants") array');
  }
  const sourceDefaults =
    value && typeof value === "object" && !Array.isArray(value) &&
    (value as Record<string, unknown>).defaults !== undefined
      ? normalizeVideoReferenceBatchDefaults(
          (value as Record<string, unknown>).defaults as VideoReferenceBatchDefaults,
        )
      : {};
  // Validate the explicit argument before spreading it. Object spread accepts
  // strings and would otherwise turn malformed defaults into numeric keys.
  const explicitDefaults = normalizeVideoReferenceBatchDefaults(defaults);
  const normalizedDefaults = normalizeVideoReferenceBatchDefaults({
    ...sourceDefaults,
    ...explicitDefaults,
  });
  const normalizedItems = normalizeVideoReferenceBatchVariants(
    value,
    options.allowRoleOnly || normalizedDefaults.promotedItemType === "none",
  );
  const sources: VideoReferenceBatchResolutionSource[] = [];

  const items = normalizedItems.map((item, index) => {
    const rawItem = sourceItems[index];
    const rawObject = rawItem && typeof rawItem === "object" && !Array.isArray(rawItem)
      ? rawItem as Record<string, unknown>
      : null;
    const rawTextStyle = rawObject?.textStyle;
    const personaId = item.personaId || normalizedDefaults.personaId;
    const characterId = item.characterId || normalizedDefaults.characterId;
    const referenceUploadId =
      item.referenceUploadId || normalizedDefaults.referenceUploadId;
    const hasItemProductId = Boolean(
      rawObject && Object.prototype.hasOwnProperty.call(rawObject, "productId"),
    );
    const hasItemCatalogVariantName = Boolean(
      rawObject &&
      (
        Object.prototype.hasOwnProperty.call(rawObject, "catalogVariantName") ||
        Object.prototype.hasOwnProperty.call(rawObject, "selectedVariantName")
      ),
    );
    if (options.enforceFormProduct && hasItemProductId) {
      throw new Error(
        `Item ${index + 1} cannot override productId; choose one Product in Set your inputs`,
      );
    }
    if (options.enforceFormProduct && hasItemCatalogVariantName) {
      throw new Error(
        `Item ${index + 1} cannot override catalogVariantName; choose one catalog variant in Set your inputs`,
      );
    }
    const selectedItemType = item.promotedItemType || normalizedDefaults.promotedItemType || "product";
    const productId = options.enforceFormProduct
      ? normalizedDefaults.productId
      : hasItemProductId ? item.productId : selectedItemType === "product" ? normalizedDefaults.productId : undefined;
    const offerId = item.offerId || (selectedItemType === "offer" ? normalizedDefaults.offerId : undefined);
    // A top-level catalog variant belongs only to the legacy top-level product.
    // Never leak it onto an entry that explicitly chooses its own product.
    const catalogVariantName = options.enforceFormProduct
      ? normalizedDefaults.catalogVariantName
      : hasItemCatalogVariantName
        ? item.catalogVariantName ?? null
        : hasItemProductId
          ? null
          : selectedItemType === "product" ? normalizedDefaults.catalogVariantName : null;
    const hasOnScreenText = item.exactText !== undefined;
    let textStyle: TextStyle | undefined = item.textStyle
      ? {
          ...item.textStyle,
          position: item.textStyle.position ? { ...item.textStyle.position } : undefined,
          stroke: item.textStyle.stroke ? { ...item.textStyle.stroke } : undefined,
          shadow: item.textStyle.shadow ? { ...item.textStyle.shadow } : undefined,
          background: item.textStyle.background ? { ...item.textStyle.background } : undefined,
        }
      : undefined;
    let fontAssetId = item.fontAssetId;
    let textPlacement = item.textPlacement;
    let musicAssetId = item.musicAssetId;
    const source: VideoReferenceBatchResolutionSource = {
      font: "none",
      placement: "none",
      alignment: "none",
      music: "automatic",
    };

    if (hasOnScreenText) {
      if (fontAssetId) {
        source.font = "json";
      } else if (rawTextStyleHasFontSource(rawTextStyle)) {
        source.font = "textStyle";
      } else if (normalizedDefaults.fontAssetId) {
        fontAssetId = normalizedDefaults.fontAssetId;
        source.font = "form";
      }

      if (textPlacement) {
        source.placement = "json";
      } else if (rawTextStyleHasPlacement(rawTextStyle)) {
        source.placement = "textStyle";
      } else if (normalizedDefaults.textPlacement && textStyle) {
        textPlacement = normalizedDefaults.textPlacement;
        textStyle = applyVideoReferenceTextPlacement(textStyle, textPlacement);
        source.placement = "form";
      }

      if (
        rawObject?.textAlign !== undefined &&
        rawObject.textAlign !== null &&
        rawObject.textAlign !== ""
      ) {
        source.alignment = "json";
      } else if (rawTextStyleHasAlignment(rawTextStyle)) {
        source.alignment = "textStyle";
      }
    }

    if (musicAssetId) {
      source.music = "json";
    } else if (normalizedDefaults.musicAssetId) {
      musicAssetId = normalizedDefaults.musicAssetId;
      source.music = "form";
    }

    sources.push(source);
    return {
      ...(personaId ? { personaId } : {}),
      ...(characterId ? { characterId } : {}),
      ...(productId ? { productId } : {}),
      ...(offerId ? { offerId } : {}),
      ...((item.promptType || normalizedDefaults.promptType) ? { promptType: item.promptType || normalizedDefaults.promptType } : {}),
      ...((item.promotedItemType || normalizedDefaults.promotedItemType) ? { promotedItemType: item.promotedItemType || normalizedDefaults.promotedItemType } : {}),
      ...(catalogVariantName !== undefined ? { catalogVariantName } : {}),
      ...(referenceUploadId ? { referenceUploadId } : {}),
      variant: item.variant,
      ...(item.exactText !== undefined ? { exactText: item.exactText } : {}),
      ...(textStyle ? { textStyle } : {}),
      ...(fontAssetId ? { fontAssetId } : {}),
      ...(textPlacement ? { textPlacement } : {}),
      ...(item.textAlign ? { textAlign: item.textAlign } : {}),
      ...(musicAssetId ? { musicAssetId } : {}),
    };
  });

  return { items, sources };
}

export const VIDEO_REFERENCE_BATCH_ITEM_EXAMPLE: VideoReferenceBatchVariant = {
  personaId: "persona-id",
  characterId: "ready-model-id",
  productId: "product-id",
  catalogVariantName: "Deep Navy",
  referenceUploadId: "reference-upload-id",
  variant: "LA nightlife",
  exactText: "30% OFF\nTHIS WEEKEND",
  fontAssetId: "font-asset-id-from-admin",
  textPlacement: "lower-third",
  musicAssetId: "music-asset-id-from-admin",
};

export const VIDEO_REFERENCE_BATCH_WORKED_EXAMPLE = {
  defaults: {
    personaId: VIDEO_REFERENCE_BATCH_ITEM_EXAMPLE.personaId,
    characterId: VIDEO_REFERENCE_BATCH_ITEM_EXAMPLE.characterId,
    productId: VIDEO_REFERENCE_BATCH_ITEM_EXAMPLE.productId,
    referenceUploadId: VIDEO_REFERENCE_BATCH_ITEM_EXAMPLE.referenceUploadId,
  },
  items: [
    VIDEO_REFERENCE_BATCH_ITEM_EXAMPLE,
    {
      variant: "Chicago rooftop",
      exactText: "NEW DROP",
      textStyle: VIDEO_REFERENCE_TEXT_STYLE_EXAMPLE,
    },
  ],
};

export function parseVideoReferenceVariantJson(
  raw: string,
): VideoReferenceBatchVariant[] {
  return normalizeVideoReferenceBatchVariants(JSON.parse(raw));
}

/** Parse once while preserving the original input for a later default merge. */
export function parseVideoReferenceVariantJsonWithSource(raw: string, allowRoleOnly = false): {
  source: unknown;
  variants: VideoReferenceBatchVariant[];
} {
  const source = JSON.parse(raw);
  return {
    source,
    variants: normalizeVideoReferenceBatchVariants(source, allowRoleOnly),
  };
}
